import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "store";
import { isADMFormat } from "utils/formats";
import { apiClient } from "module/api";

export interface AudienceInfo {
  id: number;
  name: string;
  headers: string[];
  rowsAmount: number;
  csvUrl: string;
  firstRow: string[];
}

interface AudienceDataResult {
  isADM: boolean;
  audiences: AudienceInfo[];
  loading: boolean;
  /** Parsed CSV data from the first audience (for preview) */
  audienceCsvData: Record<string, string>[] | null;
  fetchAudienceCsvData: () => Promise<Record<string, string>[] | null>;
}

/**
 * Hook that fetches audience data for the current campaign via API.
 * Works in both ADM builder and flyer builder (when opened from ADM flow).
 * Returns empty data when not in ADM mode or when no audiences are linked.
 */
export const useAudienceData = (): AudienceDataResult => {
  const { campaign } = useStore();
  const { campaignId } = useParams() as { campaignId?: string };
  const flyerType = campaign?.flyerType;
  const isADM = isADMFormat(flyerType);

  const [audiences, setAudiences] = useState<AudienceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [audienceCsvData, setAudienceCsvData] = useState<Record<string, string>[] | null>(null);

  useEffect(() => {
    if (!isADM || !campaignId) {
      setAudiences([]);
      setAudienceCsvData(null);
      return;
    }

    let cancelled = false;

    const fetchAudiences = async () => {
      setLoading(true);
      try {
        // Step 1: Get campaign-audience links
        const campaignAudiencesRes = await apiClient.getCampaignAudiences(campaignId);
        if (!campaignAudiencesRes.ok) {
          console.error("Failed to fetch campaign audiences:", campaignAudiencesRes.status);
          return;
        }
        const campaignAudiences = await campaignAudiencesRes.json();
        if (!Array.isArray(campaignAudiences) || campaignAudiences.length === 0) {
          setAudiences([]);
          return;
        }

        // Step 2: Get full audience details by IDs
        const audienceIds = campaignAudiences.map((ca: any) => ca.audience_id);
        const audienceDetails = await apiClient.getAudiencesByIds(audienceIds);
        if (cancelled) return;

        const mapped: AudienceInfo[] = (audienceDetails || []).map((a: any) => ({
          id: a.id,
          name: a.name,
          headers: a.metadata?.headers || [],
          rowsAmount: a.metadata?.rowsAmount || 0,
          csvUrl: a.csvUrl || a.csv_url || "",
          firstRow: a.metadata?.firstRow || [],
        }));

        setAudiences(mapped);
      } catch (error) {
        console.error("Error fetching audience data:", error);
        setAudiences([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAudiences();

    return () => {
      cancelled = true;
    };
  }, [isADM, campaignId]);

  /**
   * Downloads and parses the CSV from the first audience for preview.
   * Uses the audience presigned download URL endpoint.
   */
  const fetchAudienceCsvData = async (): Promise<Record<string, string>[] | null> => {
    if (audiences.length === 0 || !audiences[0].csvUrl) return null;

    try {
      // Use the audience download URL endpoint
      const downloadRes = await apiClient.getAudienceDownloadUrl({ url: audiences[0].csvUrl });
      if (!downloadRes.ok) {
        console.error("Failed to get audience CSV download URL:", downloadRes.status);
        throw new Error("Download URL failed");
      }
      const { url: signedUrl } = await downloadRes.json();
      const data = await fetchAndParseCsv(signedUrl, audiences[0].headers);
      setAudienceCsvData(data);
      return data;
    } catch (error) {
      console.error("Error fetching audience CSV data:", error);
      // Fallback: create synthetic data from firstRow
      if (audiences[0].headers.length > 0 && audiences[0].firstRow.length > 0) {
        const row: Record<string, string> = {};
        audiences[0].headers.forEach((h, i) => {
          row[h] = audiences[0].firstRow[i] || "";
        });
        const data = [row];
        setAudienceCsvData(data);
        return data;
      }
      return null;
    }
  };

  return { isADM, audiences, loading, audienceCsvData, fetchAudienceCsvData };
};

/**
 * Fetches and parses only the first ~100 rows of a CSV using streaming.
 * This avoids loading the entire file into memory (critical for 1M+ row files).
 */
async function fetchAndParseCsv(signedUrl: string, _expectedHeaders: string[]): Promise<Record<string, string>[]> {
  const MAX_ROWS = 100;

  const csvRes = await fetch(signedUrl);
  if (!csvRes.ok) throw new Error("Failed to download CSV");

  // Use streaming reader to only read what we need
  const reader = csvRes.body?.getReader();
  if (!reader) {
    // Fallback: read full text but limit parsing
    const csvText = await csvRes.text();
    return parseCsvTextLimited(csvText, MAX_ROWS);
  }

  const decoder = new TextDecoder();
  let accumulated = "";
  let lines: string[] = [];

  // Read chunks until we have enough lines (header + MAX_ROWS data rows)
  while (lines.length < MAX_ROWS + 2) {
    const { done, value } = await reader.read();
    if (done) break;
    accumulated += decoder.decode(value, { stream: true });
    lines = accumulated.split("\n");
    // If we have enough, stop reading
    if (lines.length > MAX_ROWS + 2) break;
  }

  // Cancel the rest of the download
  reader.cancel();

  if (lines.length === 0) return [];

  const headers = parseCSVLine(lines[0]).map((h) => h.replace(/\*+$/, "").trim());
  const data: Record<string, string>[] = [];

  for (let i = 1; i < Math.min(lines.length, MAX_ROWS + 1); i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseCSVLine(line);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || "";
    });
    data.push(row);
  }

  return data;
}

function parseCsvTextLimited(csvText: string, maxRows: number): Record<string, string>[] {
  const lines = csvText.split("\n");
  if (lines.length === 0) return [];

  const headers = parseCSVLine(lines[0]).map((h) => h.replace(/\*+$/, "").trim());
  const data: Record<string, string>[] = [];

  for (let i = 1; i < Math.min(lines.length, maxRows + 1); i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseCSVLine(line);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || "";
    });
    data.push(row);
  }

  return data;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}
